import { Lock } from 'iconoir-react';
import React, { FunctionComponent, ReactElement, Ref } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/shared';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import { Badge } from '@savgroup-front-common/primitive/ui/badge';
import { Spinner } from '@savgroup-front-common/primitive/ui/spinner';
import { MessageType } from '@savgroup-front-common/types';

import { safeFormattedIntlString } from '../../formatters';
import { SUPPORTED_ICON_POSITIONS } from '../../helpers/theme';
import { Tooltip } from '../Tooltip';

import {
  getCountBadgeVariant,
  getPrimitiveButtonShape,
  getPrimitiveButtonSize,
  getPrimitiveButtonVariant,
} from './Button.helpers';
import { useErrorAnimation, useHumanLoadingButton } from './Button.hooks';
import { $PrimitiveButton } from './Button.styles';

export interface ButtonComponentProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
  isError?: boolean;
  disabled?: boolean;
  dataTestId?: string;
  small?: boolean;
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  success?: boolean;
  danger?: boolean;
  alert?: boolean;
  hollow?: boolean;
  rounded?: boolean;
  underline?: boolean;
  fluid?: boolean;
  type?: BUTTON_TYPES;
  componentThemeName?: string;
  naked?: boolean;
  tabIndex?: number | undefined;
  isPressed?: boolean;
  icon?: ReactElement;
  position?: SUPPORTED_ICON_POSITIONS;
  isSuffix?: boolean;
  count?: number;
  isMinimal?: boolean;
  ariaLabel?: MessageType;
  isLocked?: boolean;
  lockedMessage?: MessageType;
  isActive?: boolean;
}

export interface ButtonProps extends ButtonComponentProps {
  forwardedRef: Ref<HTMLButtonElement>;
}

const Button: FunctionComponent<React.PropsWithChildren<ButtonProps>> = ({
  onClick = () => null,
  isLoading = false,
  isError = false,
  children,
  className,
  disabled = false,
  dataTestId,
  small = false,
  primary = false,
  secondary = false,
  tertiary = false,
  success = false,
  danger = false,
  alert = false,
  rounded = false,
  hollow = false,
  underline = false,
  fluid = false,
  type = BUTTON_TYPES.SUBMIT,
  forwardedRef,
  naked = false,
  isPressed = false,
  icon,
  position = SUPPORTED_ICON_POSITIONS.LEFT,
  componentThemeName,
  isSuffix,
  count = 0,
  isMinimal,
  ariaLabel,
  'aria-label': nativeAriaLabel,
  'aria-pressed': nativeAriaPressed,
  isLocked,
  lockedMessage,
  isActive = false,
  ...rest
}) => {
  const { isErrorAnimationReady, resetShake } = useErrorAnimation(isError);
  const isHumanLoading = useHumanLoadingButton({ isLoading });
  const isIconOnly = Boolean(icon && children === undefined);
  const iconPosition = children ? position : SUPPORTED_ICON_POSITIONS.LEFT;
  const shouldShowIcon = Boolean(icon) && !isLocked && !isHumanLoading;
  const iconWithButtonColor =
    icon &&
    React.cloneElement(icon as ReactElement<{ color?: string }>, {
      color: 'currentColor',
    });
  const isInline = Boolean(underline || isMinimal);
  const variant = getPrimitiveButtonVariant({
    primary,
    secondary,
    tertiary,
    success,
    danger,
    alert,
    hollow,
    naked,
    underline,
  });

  const button = (
    <$PrimitiveButton
      data-testid={dataTestId}
      {...rest}
      ref={forwardedRef}
      type={type}
      variant={variant}
      size={getPrimitiveButtonSize({
        small,
        isIconOnly,
        isInline,
      })}
      shape={getPrimitiveButtonShape({ rounded, isSuffix })}
      className={cn(
        fluid && 'w-full shrink',
        count > 0 && 'relative',
        className,
      )}
      data-active={isActive || undefined}
      aria-pressed={isPressed || nativeAriaPressed}
      data-theme-name={componentThemeName}
      aria-label={
        ariaLabel ? safeFormattedIntlString(ariaLabel) : nativeAriaLabel
      }
      disabled={isLoading || disabled || isLocked}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        resetShake();
        onClick(e);
      }}
      $isShaking={isErrorAnimationReady}
    >
      {isHumanLoading && <Spinner data-icon="inline-start" />}
      {isLocked && !isHumanLoading && <Lock data-icon="inline-start" />}
      {shouldShowIcon && iconPosition === SUPPORTED_ICON_POSITIONS.LEFT && (
        <span data-icon="inline-start" className="contents">
          {iconWithButtonColor}
        </span>
      )}
      {children}
      {shouldShowIcon && iconPosition === SUPPORTED_ICON_POSITIONS.RIGHT && (
        <span data-icon="inline-end" className="contents">
          {iconWithButtonColor}
        </span>
      )}
      {count > 0 && (
        <Badge
          variant={getCountBadgeVariant(variant)}
          size="xs"
          shape="square"
          className={cn(
            !isInline && 'absolute right-0 bottom-0',
            !isInline && small && '-right-1 -bottom-1',
          )}
        >
          {count}
        </Badge>
      )}
    </$PrimitiveButton>
  );

  if (!isLocked) {
    return button;
  }

  return (
    <Tooltip content={safeFormattedIntlString(lockedMessage)}>
      <span className={cn('inline-flex', fluid && 'w-full')}>{button}</span>
    </Tooltip>
  );
};

export default React.forwardRef<HTMLButtonElement, ButtonComponentProps>(
  (props, ref) => <Button forwardedRef={ref} {...props} />,
);
